import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import EpisodeCard from './EpisodeCard';
import { channelInfo } from '../data/podcastData';

const PodcastModal = ({ isOpen, onClose, episodes }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(12); // Increased density

  useEffect(() => {
    const handleResize = () => {
      // Much higher density for "View All"
      if (window.innerWidth < 640) setItemsPerPage(4);
      else if (window.innerWidth < 1024) setItemsPerPage(8);
      else setItemsPerPage(12);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalPages = Math.ceil(episodes.length / itemsPerPage);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % totalPages);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);

  // Reset to first page when modal opens
  useEffect(() => {
    if (isOpen) setCurrentIndex(0);
  }, [isOpen]);

  if (!isOpen) return null;

  const currentEpisodes = episodes.slice(
    currentIndex * itemsPerPage,
    (currentIndex * itemsPerPage) + itemsPerPage
  );

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 md:p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-brand-dark/90 backdrop-blur-sm"
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-brand-cream w-full max-w-7xl rounded-xl shadow-2xl overflow-hidden h-[90vh] flex flex-col"
        >
          {/* Modal Header */}
          <div className="relative bg-brand-dark text-white p-6 flex-shrink-0 flex justify-between items-center">
             <div className="flex items-center gap-4">
                <img 
                  src={channelInfo.image} 
                  alt={channelInfo.title} 
                  className="w-12 h-12 rounded border border-brand-tan/30 object-cover" 
                />
                <div>
                  <h2 className="text-xl font-bold font-serif">{channelInfo.title}</h2>
                  <p className="text-brand-tan text-xs uppercase tracking-widest">Full Archive</p>
                </div>
             </div>

            <button
              onClick={onClose}
              className="bg-white/10 hover:bg-brand-tan p-2 rounded-full text-white transition-all"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          {/* Grid Content */}
          <div className="p-4 sm:p-8 flex-grow overflow-y-auto custom-scrollbar bg-brand-cream">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-brand-dark flex items-center gap-2">
                All Episodes <span className="text-brand-green/60 text-sm font-normal">({episodes.length})</span>
              </h3>
              
              {totalPages > 1 && (
                <div className="flex gap-2">
                  <button onClick={prevSlide} className="p-2 rounded-full border border-brand-dark/10 hover:bg-brand-tan hover:text-white transition-colors">
                    <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
                  </button>
                  <button onClick={nextSlide} className="p-2 rounded-full border border-brand-dark/10 hover:bg-brand-tan hover:text-white transition-colors">
                    <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              {currentEpisodes.map((episode, idx) => (
                <EpisodeCard 
                  key={`${currentIndex}-${idx}`} 
                  episode={episode} 
                  episodeList={episodes} // Pass the full list here!
                />
              ))}
            </motion.div>

            {/* Pagination Dots */}
            {totalPages > 1 && (
              <div className="flex justify-center flex-wrap gap-2 mt-8 pb-4">
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      idx === currentIndex ? 'bg-brand-tan w-8' : 'bg-brand-green/20 w-2 hover:bg-brand-green/40'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PodcastModal;